import React, { useState } from "react"
import {
  Card,
  CardBody,
  Col,
  Form,
  Input,
  Label,
  Row,
  Spinner,
  Table,
  Badge,
  Button,
} from "reactstrap"
import QdContainer from "components/Common/QdContainer"
import Cookies from "js-cookie"
import TenantAuthConfig from "pages/CRSManagement/TenantConnections/TenantAuthConfig"
import EmailDomainsEditor from "./EmailDomainsEditor"

function OnboardingStatus() {
  const [scac, setScac] = useState("")
  const [results, setResults] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [submittedScac, setSubmittedScac] = useState(null)

  // Credentials editor (username / password / api_key) — TURVO only
  const [creds, setCreds] = useState(null)
  const [credForm, setCredForm] = useState({
    username: "",
    password: "",
    api_key: "",
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showApiKey, setShowApiKey] = useState(false)
  const [savingCreds, setSavingCreds] = useState(false)
  const [credMsg, setCredMsg] = useState(null)
  const [credError, setCredError] = useState(null)

  // Turvo feature flags — top-level keys in the tenant connection config.
  // Unlike credentials, these are written explicitly on save (a boolean has no
  // "empty = unchanged" sentinel), so the form mirrors the saved flag state.
  const [flagForm, setFlagForm] = useState({
    inbound_item_lot_enrichment: false,
    inbound_appt_date_source: "",
    skip_qued_reference_write: false,
  })
  const [savingFlags, setSavingFlags] = useState(false)
  const [flagMsg, setFlagMsg] = useState(null)

  const api_url = process.env.REACT_APP_API_URL
  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${Cookies.get("idToken")}`,
  })

  const eyeBtnStyle = {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    backgroundColor: "#f8f9fa",
    border: "1px solid #ced4da",
    color: "#495057",
  }

  const emptyFlags = {
    inbound_item_lot_enrichment: false,
    inbound_appt_date_source: "",
    skip_qued_reference_write: false,
  }

  const loadCredentials = async scacCode => {
    setCreds(null)
    setCredError(null)
    setCredForm({ username: "", password: "", api_key: "" })
    setFlagForm(emptyFlags)
    setShowPassword(false)
    setShowApiKey(false)
    try {
      const res = await fetch(
        `${api_url}crs/tenant-onboarding?scac=${encodeURIComponent(
          scacCode
        )}&section=credentials`,
        { method: "GET", headers: authHeaders(), credentials: "include" }
      )
      if (!res.ok) {
        setCredError("Could not load credentials for this tenant.")
        return
      }
      const data = await res.json()
      setCreds(data)
      if (data && data.found) {
        setCredForm({
          username: data.username || "",
          password: data.password || "",
          api_key: data.api_key || "",
        })
        if (data.flags) {
          setFlagForm({
            inbound_item_lot_enrichment:
              !!data.flags.inbound_item_lot_enrichment,
            inbound_appt_date_source: data.flags.inbound_appt_date_source || "",
            skip_qued_reference_write: !!data.flags.skip_qued_reference_write,
          })
        }
      }
    } catch (err) {
      console.error("Failed to load credentials:", err)
      setCredError("Could not load credentials for this tenant.")
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setSubmittedScac(null)
    setResults(null)
    setCreds(null)
    setCredMsg(null)
    setFlagMsg(null)
    setCredError(null)

    try {
      const res = await fetch(
        `${api_url}crs/tenant-onboarding?scac=${encodeURIComponent(scac)}`,
        {
          method: "GET",
          headers: authHeaders(),
          credentials: "include",
        }
      )

      if (!res.ok)
        throw new Error(`Request failed: ${res.status} ${res.statusText}`)
      const data = await res.json()
      setResults(data)
      setSubmittedScac(scac)
      await loadCredentials(scac)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleCredChange = e => {
    const { name, value } = e.target
    setCredForm(prev => ({ ...prev, [name]: value }))
  }

  const handleSaveCreds = async () => {
    setSavingCreds(true)
    setCredMsg(null)
    try {
      // Only send non-empty fields: this editor fills/updates values, it never
      // blanks them, so an empty field is left unchanged server-side.
      const payload = {}
      if (credForm.username) payload.username = credForm.username
      if (credForm.password) payload.password = credForm.password
      if (credForm.api_key) payload.api_key = credForm.api_key
      const res = await fetch(
        `${api_url}crs/tenant-onboarding?scac=${encodeURIComponent(
          creds.scac
        )}`,
        {
          method: "PUT",
          headers: authHeaders(),
          credentials: "include",
          body: JSON.stringify(payload),
        }
      )
      if (!res.ok)
        throw new Error(`Request failed: ${res.status} ${res.statusText}`)
      const data = await res.json()
      const ok =
        Array.isArray(data) &&
        data.length > 0 &&
        data.every(r => r.status === "success")
      const firstError = Array.isArray(data)
        ? data.find(r => r.status !== "success")
        : null
      setCredMsg({
        text: ok
          ? (data[0] && data[0].message) || "Saved"
          : (firstError && firstError.message) || "Save failed",
        ok,
      })
      await loadCredentials(creds.scac)
    } catch (err) {
      setCredMsg({ text: err.message, ok: false })
    } finally {
      setSavingCreds(false)
    }
  }

  const handleFlagToggle = e => {
    const { name, checked } = e.target
    setFlagForm(prev => ({ ...prev, [name]: checked }))
  }

  const handleFlagSelect = e => {
    const { name, value } = e.target
    setFlagForm(prev => ({ ...prev, [name]: value }))
  }

  const handleSaveFlags = async () => {
    setSavingFlags(true)
    setFlagMsg(null)
    try {
      // Flags are written explicitly (always sent). The payload carries only
      // flag keys, so saving flags never touches stored credentials.
      const payload = {
        inbound_item_lot_enrichment: flagForm.inbound_item_lot_enrichment,
        inbound_appt_date_source: flagForm.inbound_appt_date_source,
        skip_qued_reference_write: flagForm.skip_qued_reference_write,
      }
      const res = await fetch(
        `${api_url}crs/tenant-onboarding?scac=${encodeURIComponent(
          creds.scac
        )}`,
        {
          method: "PUT",
          headers: authHeaders(),
          credentials: "include",
          body: JSON.stringify(payload),
        }
      )
      if (!res.ok)
        throw new Error(`Request failed: ${res.status} ${res.statusText}`)
      const data = await res.json()
      const ok =
        Array.isArray(data) &&
        data.length > 0 &&
        data.every(r => r.status === "success")
      const firstError = Array.isArray(data)
        ? data.find(r => r.status !== "success")
        : null
      setFlagMsg({
        text: ok
          ? (data[0] && data[0].message) || "Saved"
          : (firstError && firstError.message) || "Save failed",
        ok,
      })
      await loadCredentials(creds.scac)
    } catch (err) {
      setFlagMsg({ text: err.message, ok: false })
    } finally {
      setSavingFlags(false)
    }
  }

  const successCount = results
    ? results.filter(r => r.status === "success").length
    : 0
  const errorCount = results
    ? results.filter(r => r.status === "error").length
    : 0
  const showCredEditor = creds && creds.found && creds.api_type === "TURVO"

  const sectionHeadingStyle = {
    fontSize: "14px",
    fontWeight: "600",
    borderBottom: "2px solid #EE5A29",
    paddingBottom: "6px",
    display: "inline-block",
    marginBottom: "20px",
    color: "#EE5A29",
  }

  return (
    <QdContainer>
      <Row>
        <Col lg={7}>
          <Card className="p-3 shadow-sm">
            <CardBody className="p-0">
              <h6
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  borderBottom: "2px solid #EE5A29",
                  paddingBottom: "6px",
                  display: "inline-block",
                  marginBottom: "20px",
                  color: "#EE5A29",
                }}
              >
                Onboarding Status
              </h6>
              <Form onSubmit={handleSubmit} className="p-2">
                <Row className="mb-4">
                  <Label className="col-sm-3 col-form-label">SCAC Code</Label>
                  <Col sm={6}>
                    <Input
                      type="text"
                      className="form-control"
                      value={scac}
                      onChange={e => setScac(e.target.value.toUpperCase())}
                      required
                    />
                  </Col>
                </Row>
                <Row className="mb-4">
                  <Col sm={{ offset: 3 }}>
                    <button
                      type="submit"
                      className="btn btn-primary btn-sm"
                      disabled={loading}
                    >
                      {loading ? <Spinner size="sm" /> : "Submit"}
                    </button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {error && (
        <Row className="mt-3">
          <Col lg={7}>
            <div className="alert alert-danger" style={{ fontSize: "13px" }}>
              {error}
            </div>
          </Col>
        </Row>
      )}

      {credError && (
        <Row className="mt-3">
          <Col lg={7}>
            <div className="alert alert-warning" style={{ fontSize: "13px" }}>
              {credError}
            </div>
          </Col>
        </Row>
      )}

      {results && (
        <Row className="mt-3">
          <Col lg={7}>
            <Card className="shadow-sm">
              <CardBody className="p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      borderBottom: "2px solid #EE5A29",
                      paddingBottom: "6px",
                      display: "inline-block",
                      color: "#EE5A29",
                      marginBottom: 0,
                    }}
                  >
                    Results
                  </h6>
                  <span className="text-muted" style={{ fontSize: "12px" }}>
                    {results.length} checks &nbsp;·&nbsp;
                    <span style={{ color: "#34c38f", fontWeight: 600 }}>
                      {successCount} passed
                    </span>
                    &nbsp;·&nbsp;
                    <span style={{ color: "#f46a6a", fontWeight: 600 }}>
                      {errorCount} failed
                    </span>
                  </span>
                </div>
                <Table
                  hover
                  responsive
                  className="mb-0"
                  style={{
                    fontFamily: "Inter, Poppins, sans-serif",
                    fontSize: "13px",
                  }}
                >
                  <thead className="table-light">
                    <tr>
                      <th style={{ fontWeight: "600", width: "85%" }}>Check</th>
                      <th
                        style={{
                          fontWeight: "600",
                          width: "15%",
                          textAlign: "center",
                        }}
                      >
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((item, idx) => {
                      const isSuccess = item.status === "success"
                      return (
                        <tr key={idx}>
                          <td
                            style={{
                              verticalAlign: "middle",
                              color: isSuccess ? "#1a7a55" : "#c0392b",
                            }}
                          >
                            {item.message}
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              verticalAlign: "middle",
                            }}
                          >
                            <Badge
                              color={isSuccess ? "success" : "danger"}
                              style={{
                                fontSize: "11px",
                                padding: "4px 8px",
                                minWidth: "52px",
                                display: "inline-block",
                              }}
                            >
                              {isSuccess ? "OK" : "Error"}
                            </Badge>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {submittedScac && (
        <Row className="mt-3">
          <Col lg={7}>
            <EmailDomainsEditor scac={submittedScac} />
          </Col>
        </Row>
      )}

      {showCredEditor && (
        <Row className="mt-3">
          <Col lg={7}>
            <Card className="shadow-sm">
              <CardBody className="p-3">
                <h6 style={sectionHeadingStyle}>Update Turvo Credentials</h6>

                <Row className="mb-3">
                  <Label
                    htmlFor="cred-username"
                    className="col-sm-3 col-form-label"
                  >
                    Username
                  </Label>
                  <Col sm={7}>
                    <Input
                      id="cred-username"
                      type="text"
                      name="username"
                      value={credForm.username}
                      onChange={handleCredChange}
                      placeholder="Enter username"
                    />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Label
                    htmlFor="cred-password"
                    className="col-sm-3 col-form-label"
                  >
                    Password
                  </Label>
                  <Col sm={7}>
                    <div className="d-flex">
                      <Input
                        id="cred-password"
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={credForm.password}
                        onChange={handleCredChange}
                        placeholder="Enter password"
                        style={{
                          borderTopRightRadius: 0,
                          borderBottomRightRadius: 0,
                        }}
                      />
                      <Button
                        type="button"
                        onClick={() => setShowPassword(s => !s)}
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        aria-pressed={showPassword}
                        style={eyeBtnStyle}
                      >
                        {showPassword ? "🙈" : "👁️"}
                      </Button>
                    </div>
                    <small className="text-muted">
                      Click the eye icon to reveal. Edit to change it.
                    </small>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Label
                    htmlFor="cred-api-key"
                    className="col-sm-3 col-form-label"
                  >
                    API Key
                  </Label>
                  <Col sm={7}>
                    <div className="d-flex">
                      <Input
                        id="cred-api-key"
                        type={showApiKey ? "text" : "password"}
                        name="api_key"
                        value={credForm.api_key}
                        onChange={handleCredChange}
                        placeholder="Enter Turvo API key"
                        style={{
                          borderTopRightRadius: 0,
                          borderBottomRightRadius: 0,
                        }}
                      />
                      <Button
                        type="button"
                        onClick={() => setShowApiKey(s => !s)}
                        aria-label={
                          showApiKey ? "Hide API key" : "Show API key"
                        }
                        aria-pressed={showApiKey}
                        style={eyeBtnStyle}
                      >
                        {showApiKey ? "🙈" : "👁️"}
                      </Button>
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col sm={{ offset: 3 }}>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={handleSaveCreds}
                      disabled={savingCreds}
                    >
                      {savingCreds ? <Spinner size="sm" /> : "Save"}
                    </button>
                    {credMsg && (
                      <span
                        className="ms-3"
                        style={{
                          fontSize: "13px",
                          color: credMsg.ok ? "#1a7a55" : "#c0392b",
                        }}
                      >
                        {credMsg.text}
                      </span>
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {showCredEditor && (
        <Row className="mt-3">
          <Col lg={7}>
            <Card className="shadow-sm">
              <CardBody className="p-3">
                <h6 style={sectionHeadingStyle}>Turvo Feature Flags</h6>

                <Row className="mb-3">
                  <Label
                    htmlFor="flag-item-lot"
                    className="col-sm-5 col-form-label"
                  >
                    Item / Lot enrichment
                  </Label>
                  <Col sm={7}>
                    <div
                      className="form-check form-switch"
                      style={{ paddingTop: "6px" }}
                    >
                      <Input
                        id="flag-item-lot"
                        type="switch"
                        role="switch"
                        name="inbound_item_lot_enrichment"
                        checked={flagForm.inbound_item_lot_enrichment}
                        onChange={handleFlagToggle}
                      />
                    </div>
                    <small className="text-muted">
                      Fetch the full shipment to enrich appointments with
                      item/lot details (one extra Turvo call per webhook).
                    </small>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Label
                    htmlFor="flag-skip-qued-ref"
                    className="col-sm-5 col-form-label"
                  >
                    Skip QUED reference write
                  </Label>
                  <Col sm={7}>
                    <div
                      className="form-check form-switch"
                      style={{ paddingTop: "6px" }}
                    >
                      <Input
                        id="flag-skip-qued-ref"
                        type="switch"
                        role="switch"
                        name="skip_qued_reference_write"
                        checked={flagForm.skip_qued_reference_write}
                        onChange={handleFlagToggle}
                      />
                    </div>
                    <small className="text-muted">
                      Skip writing the QUED reference external id onto the Turvo
                      customer order (outbound).
                    </small>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Label
                    htmlFor="flag-appt-date-source"
                    className="col-sm-5 col-form-label"
                  >
                    Appointment date source
                  </Label>
                  <Col sm={7}>
                    <Input
                      id="flag-appt-date-source"
                      type="select"
                      name="inbound_appt_date_source"
                      value={flagForm.inbound_appt_date_source}
                      onChange={handleFlagSelect}
                    >
                      <option value="">Default (appointment object)</option>
                      <option value="plannedRequestedDate">
                        plannedRequestedDate
                      </option>
                    </Input>
                    <small className="text-muted">
                      Which Turvo stop field the normalized appointment window
                      is derived from (inbound).
                    </small>
                  </Col>
                </Row>

                <Row>
                  <Col sm={{ offset: 5 }}>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={handleSaveFlags}
                      disabled={savingFlags}
                    >
                      {savingFlags ? <Spinner size="sm" /> : "Save"}
                    </button>
                    {flagMsg && (
                      <span
                        className="ms-3"
                        style={{
                          fontSize: "13px",
                          color: flagMsg.ok ? "#1a7a55" : "#c0392b",
                        }}
                      >
                        {flagMsg.text}
                      </span>
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {submittedScac &&
        creds &&
        creds.found &&
        ["QUED_API", "REVENOVA", "MC_API", "TAI", "ALVYS", "3G"].includes(
          creds.api_type
        ) && (
          <Row className="mt-3">
            <Col lg={7}>
              <h6 style={sectionHeadingStyle}>Outbound Authentication</h6>
              <TenantAuthConfig scac={submittedScac} />
            </Col>
          </Row>
        )}
    </QdContainer>
  )
}

export default OnboardingStatus
